import { isDefined } from "@clipboard-health/util-ts";
import { apiClient } from "@src/appV2/api/api";
import { API_TYPES } from "@src/appV2/api/apiTypes";
import {
  type IncludedWorkerSchema,
  type ShiftData,
  type ShiftListQuery,
  type ShiftsListResponse,
  shiftsListResponseSchema,
} from "@src/appV2/Shifts/api/useGetShifts";
import { useQueries } from "@tanstack/react-query";
import { addDays, subDays } from "date-fns";
import { stringify } from "qs";

const MAX_PAST_SHIFTS = 5;
const MAX_FUTURE_SHIFTS = 5;
const PAST_DAYS_RANGE = 3;
const FUTURE_DAYS_RANGE = 7;

export interface WorkplaceShift {
  id: string;
  qualificationName: string | undefined;
  startAt: string | undefined;
  endAt: string | undefined;
  workerName: string | undefined;
  workerId: string | undefined;
  workplaceTimezone: string | undefined;
  hasClockIn: boolean;
  hasClockOut: boolean;
}

export interface UseGetWorkplaceShiftsResult {
  shifts: WorkplaceShift[];
  isLoading: boolean;
  hasShifts: boolean;
}

interface UseGetWorkplaceShiftsParams {
  workplaceId: string | undefined;
  enabled?: boolean;
}

function getWorkerFromIncluded(
  workerId: string,
  included: ShiftsListResponse["included"]
): IncludedWorkerSchema | undefined {
  if (!isDefined(included)) {
    return undefined;
  }

  return included.find(
    (item): item is IncludedWorkerSchema => item.type === API_TYPES.worker && item.id === workerId
  );
}

function mapShiftToWorkplaceShift(
  shift: ShiftData,
  included: ShiftsListResponse["included"],
  workplaceTimezone: string | undefined
): WorkplaceShift {
  const workerId = shift.relationships?.worker?.data?.id;
  const worker = isDefined(workerId) ? getWorkerFromIncluded(workerId, included) : undefined;

  return {
    id: shift.id,
    qualificationName: shift.attributes?.requirements?.qualificationName,
    startAt: shift.attributes?.schedule?.startAt,
    endAt: shift.attributes?.schedule?.endAt,
    workerName: worker?.attributes?.name,
    workerId,
    workplaceTimezone,
    hasClockIn: isDefined(shift.attributes?.clockInOut?.start),
    hasClockOut: isDefined(shift.attributes?.clockInOut?.end),
  };
}

function buildShiftQuery(params: {
  workplaceId: string;
  startGt?: Date;
  startLt?: Date;
  sortOrder: "asc" | "desc";
  pageSize: number;
}): ShiftListQuery {
  const { workplaceId, startGt, startLt, sortOrder, pageSize } = params;

  return {
    filter: {
      facilityId: workplaceId,
      start: {
        ...(isDefined(startGt) && { gt: startGt }),
        ...(isDefined(startLt) && { lt: startLt }),
      },
    },
    fields: {
      shift: [
        "schedule.startAt",
        "schedule.endAt",
        "requirements.qualificationName",
        "clockInOut.start",
        "clockInOut.end",
      ],
      workplace: ["name", "timezone"],
      worker: ["name"],
    },
    include: [API_TYPES.workplace, API_TYPES.worker],
    sort: {
      field: "schedule.startAt",
      order: sortOrder,
    },
    page: {
      size: pageSize,
    },
  };
}

async function fetchShifts(query: ShiftListQuery): Promise<ShiftsListResponse> {
  const response = await apiClient.get<ShiftsListResponse>("/v3/shifts", {
    params: query,
    paramsSerializer: (params) => stringify(params, { arrayFormat: "comma" }),
  });

  return shiftsListResponseSchema.parse(response.data);
}

function getWorkplaceTimezoneFromResponse(response: ShiftsListResponse): string | undefined {
  const workplace = response.included?.find((item) => item.type === API_TYPES.workplace);
  if (workplace?.type === API_TYPES.workplace) {
    return workplace.attributes?.timezone;
  }
  return undefined;
}

export function useGetWorkplaceShifts(
  params: UseGetWorkplaceShiftsParams
): UseGetWorkplaceShiftsResult {
  const { workplaceId, enabled = true } = params;

  const isQueryEnabled = enabled && isDefined(workplaceId);

  const results = useQueries({
    queries: [
      {
        queryKey: ["workplaceShifts", "past", workplaceId],
        queryFn: async () => {
          const now = new Date();
          const pastStartDate = subDays(now, PAST_DAYS_RANGE);
          const query = buildShiftQuery({
            workplaceId: workplaceId ?? "",
            startGt: pastStartDate,
            startLt: now,
            sortOrder: "desc",
            pageSize: MAX_PAST_SHIFTS,
          });
          return await fetchShifts(query);
        },
        enabled: isQueryEnabled,
        staleTime: 30_000,
      },
      {
        queryKey: ["workplaceShifts", "future", workplaceId],
        queryFn: async () => {
          const now = new Date();
          const futureEndDate = addDays(now, FUTURE_DAYS_RANGE);
          const query = buildShiftQuery({
            workplaceId: workplaceId ?? "",
            startGt: now,
            startLt: futureEndDate,
            sortOrder: "asc",
            pageSize: MAX_FUTURE_SHIFTS,
          });
          return await fetchShifts(query);
        },
        enabled: isQueryEnabled,
        staleTime: 30_000,
      },
    ],
  });

  const [pastResult, futureResult] = results;
  const isLoading = pastResult.isLoading || futureResult.isLoading;

  const shifts: WorkplaceShift[] = [];

  if (isDefined(pastResult.data)) {
    const { data: pastShiftsData, included: pastIncluded } = pastResult.data;
    const workplaceTimezone = getWorkplaceTimezoneFromResponse(pastResult.data);
    const pastShifts = pastShiftsData
      .slice(0, MAX_PAST_SHIFTS)
      .map((shift) => mapShiftToWorkplaceShift(shift, pastIncluded, workplaceTimezone))
      .reverse();
    shifts.push(...pastShifts);
  }

  if (isDefined(futureResult.data)) {
    const { data: futureShiftsData, included: futureIncluded } = futureResult.data;
    const workplaceTimezone = getWorkplaceTimezoneFromResponse(futureResult.data);
    const futureShifts = futureShiftsData
      .slice(0, MAX_FUTURE_SHIFTS)
      .map((shift) => mapShiftToWorkplaceShift(shift, futureIncluded, workplaceTimezone));
    shifts.push(...futureShifts);
  }

  return {
    shifts,
    isLoading,
    hasShifts: shifts.length > 0,
  };
}
